import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import ReactDOM from 'react-dom/client';
import React from 'react';
import { renderReactSection } from '../pdf/renderReactSection';

// Import print-friendly chart components
import PrintStandardsDriftChart from '../../components/qaqc/reports/print/PrintStandardsDriftChart';
import PrintStandardsTrendChart from '../../components/qaqc/reports/print/PrintStandardsTrendChart';
import PrintControlStandardsChart from '../../components/qaqc/reports/print/PrintControlStandardsChart';
import PrintBlanksChart from '../../components/qaqc/reports/print/PrintBlanksChart';
import PrintDuplicatesChart from '../../components/qaqc/reports/print/PrintDuplicatesChart';
import PrintSummaryStats from '../../components/qaqc/reports/print/PrintSummaryStats';
import PrintFailuresTable from '../../components/qaqc/reports/print/PrintFailuresTable';
import { buildReportSummaryForPrint } from './reportSummaryStats';

/**
 * Generate QAQC PDF Report
 * @param {Object} options - Generation options
 * @param {Object} options.filteredData - Date-filtered data for all sections
 * @param {Object} options.selectedSections - Which sections to include
 * @param {Array} options.selectedElements - Which elements to report on
 * @param {string} options.reportTitle - Report title
 * @param {string} options.dateRangeSummary - Date range text
 * @param {Object} options.dashboardSummary - Dashboard summary statistics
 * @param {Function} options.onProgress - Progress callback function
 * @param {HTMLElement} options.printContainerRef - Container for rendering charts
 * @returns {Promise<void>} Downloads the PDF
 */
export const generateQAQCReport = async (options) => {
  const { 
    filteredData, 
    selectedSections, 
    selectedElements,
    reportTitle,
    dateRangeSummary,
    dashboardSummary,
    onProgress,
    printContainerRef
  } = options;

  try {
    onProgress('Initializing PDF document...');
    
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - (2 * margin);
    
    let pageNumber = 1;

    // Helper function to add page header
    const addPageHeader = () => {
      pdf.setFontSize(8);
      pdf.setTextColor(128, 128, 128);
      pdf.text(reportTitle, margin, 10);
      pdf.text(`Page ${pageNumber}`, pageWidth - margin, 10, { align: 'right' });
      pdf.setTextColor(0, 0, 0);
      pageNumber++;
    };

    // Add title page
    onProgress('Creating title page...');
    pdf.setFontSize(24);
    pdf.setFont(undefined, 'bold');
    pdf.text(reportTitle, pageWidth / 2, 80, { align: 'center' });
    
    pdf.setFontSize(14);
    pdf.setFont(undefined, 'normal');
    pdf.text(dateRangeSummary, pageWidth / 2, 95, { align: 'center' });
    
    pdf.setFontSize(10);
    pdf.setTextColor(100, 100, 100);
    pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 110, { align: 'center' });
    pdf.setTextColor(0, 0, 0);

    // Add summary stats section (metrics derived from date-filtered data + API snapshot for unresolved failures when needed)
    if (selectedSections.summaryStats) {
      const summaryForPrint = buildReportSummaryForPrint(filteredData, dashboardSummary);
      pdf.addPage();
      addPageHeader();
      onProgress('Adding summary statistics...');
      await renderReactSection(
        pdf, 
        PrintSummaryStats, 
        { dashboardSummary: summaryForPrint }, 
        printContainerRef, 
        margin, 
        contentWidth
      );
    }

    // Add Standards Drift charts
    if (selectedSections.standardsDrift && filteredData.standardsDrift) {
      for (const element of selectedElements) {
        const elementData = filteredData.standardsDrift.filter(d => d.element === element);
        if (elementData.length > 0) {
          pdf.addPage();
          addPageHeader();
          onProgress(`Rendering Standards Drift - ${element}...`);
          await renderReactSection(
            pdf, 
            PrintStandardsDriftChart, 
            { data: elementData, element, crmCode: 'all' }, 
            printContainerRef, 
            margin, 
            contentWidth
          );
        }
      }
    }

    // Add Standards Trend charts
    if (selectedSections.standardsTrend && filteredData.standardsBiasMonth) {
      for (const element of selectedElements) {
        const elementData = filteredData.standardsBiasMonth.filter(d => d.element === element);
        if (elementData.length > 0) {
          pdf.addPage();
          addPageHeader();
          onProgress(`Rendering Standards Trend - ${element}...`);
          await renderReactSection(
            pdf, 
            PrintStandardsTrendChart, 
            { data: elementData, element }, 
            printContainerRef, 
            margin, 
            contentWidth
          );
        }
      }
    }

    // Add Control Standards charts
    if (selectedSections.controlStandards && filteredData.controlStandards) {
      for (const element of selectedElements) {
        const elementData = filteredData.controlStandards.filter(d => d.element === element);
        if (elementData.length > 0) {
          pdf.addPage();
          addPageHeader();
          onProgress(`Rendering Control Standards - ${element}...`);
          await renderReactSection(
            pdf, 
            PrintControlStandardsChart, 
            { data: elementData, element, crmCode: 'all' }, 
            printContainerRef, 
            margin, 
            contentWidth
          );
        }
      }
    }

    // Add Blanks Analysis charts
    if (selectedSections.blanksAnalysis && filteredData.blanksDrift) {
      for (const element of selectedElements) {
        const elementData = filteredData.blanksDrift.filter(d => d.element === element);
        if (elementData.length > 0) {
          pdf.addPage();
          addPageHeader();
          onProgress(`Rendering Blanks Analysis - ${element}...`);
          await renderReactSection(
            pdf, 
            PrintBlanksChart, 
            { data: elementData, element }, 
            printContainerRef, 
            margin, 
            contentWidth
          );
        }
      }
    }

    // Add Duplicates Precision charts
    if (selectedSections.duplicatesPrecision && filteredData.duplicatesDrift) {
      for (const element of selectedElements) {
        const elementData = filteredData.duplicatesDrift.filter(d => d.element === element);
        if (elementData.length > 0) {
          pdf.addPage();
          addPageHeader();
          onProgress(`Rendering Duplicates Precision - ${element}...`);
          await renderReactSection(
            pdf, 
            PrintDuplicatesChart, 
            { data: elementData, element }, 
            printContainerRef, 
            margin, 
            contentWidth
          );
        }
      }
    }

    // Add Failures Table
    if (selectedSections.failuresTable && filteredData.qaqcFailures) {
      const unresolvedFailures = filteredData.qaqcFailures.filter(f => !f.resolved);
      if (unresolvedFailures.length > 0) {
        pdf.addPage();
        addPageHeader();
        onProgress('Adding failures table...');
        await renderReactSection(
          pdf, 
          PrintFailuresTable, 
          { failures: unresolvedFailures, maxRows: 50 }, 
          printContainerRef, 
          margin, 
          contentWidth
        );
      }
    }

    // Save PDF
    onProgress('Finalizing PDF...');
    const filename = `QAQC_Report_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(filename);
    
    onProgress('Download complete!');
  } catch (error) {
    console.error('Error in generateQAQCReport:', error);
    throw error;
  }
};

/**
 * Capture a React component as canvas image
 * @param {React.Component} Component - React component to render
 * @param {Object} props - Props for the component
 * @param {HTMLElement} container - Container to render in
 * @returns {Promise<HTMLCanvasElement>} Canvas element
 */
export const captureComponentAsCanvas = async (Component, props, container) => {
  return new Promise((resolve, reject) => {
    try {
      // Create a temporary root and render the component
      const root = ReactDOM.createRoot(container);
      root.render(React.createElement(Component, props));
      
      // Wait for component to render
      setTimeout(async () => {
        try {
          const canvas = await html2canvas(container, {
            backgroundColor: '#ffffff',
            scale: 2, // Higher quality
            logging: false,
            useCORS: true
          });
          
          // Cleanup
          root.unmount();
          container.innerHTML = '';
          
          resolve(canvas);
        } catch (error) {
          reject(error);
        }
      }, 1000); // Give charts time to render
    } catch (error) {
      reject(error);
    }
  });
};

export default generateQAQCReport;
